// // https://en.wikipedia.org/wiki/Well-known_text_representation_of_geometry

// We only care about 2D WKT data for now
// Coordinate order is (x, y) or (longitude, latitude)
export type WktPoint = [number, number];

// We only care about supporting these data types for now
export enum WktDataType {
    Point = "POINT",
    MultiPoint = "MULTIPOINT",
    LineString = "LINESTRING",
    MultiLineString = "MULTILINESTRING",
    Polygon = "POLYGON",
    MultiPolygon = "MULTIPOLYGON",
    GeometryCollection = "GEOMETRYCOLLECTION",
};

export type WktData = (
    null | // EMPTY
    WktPoint | // POINT
    WktPoint[] | // LINESTRING, MULTIPOINT
    WktPoint[][] | // PLOYGON, MULTILINESTRING
    WktPoint[][][] | // MULTIPOLYGON
    Wkt[] // GEOMETRYCOLLECTION
);

// Serialize a single WKT (x, y) or (lon, lat) point
export function wktPointToString(point: WktPoint): string {
    return `${point[0]} ${point[1]}`;
}

// Serialize a list of WKT points, for example a LineString
export function wktPointListToString(points: WktPoint[]): string {
    return "(" + points.map(wktPointToString).join(", ") + ")";
}

// Serialize a list of lists of WKT points, for example a Polygon
export function wktPointListListToString(points: WktPoint[][]): string {
    return "(" + points.map(wktPointListToString).join(", ") + ")";
}

// Serialize a list of lists of lists of WKT points, for example a MultiPolygon
export function wktPointListListListToString(points: WktPoint[][][]): string {
    return "(" + points.map(wktPointListListToString).join(", ") + ")";
}

export class Wkt {
    type: WktDataType;
    data: WktData;
    
    constructor(type: WktDataType, data: WktData) {
        this.type = type;
        this.data = data;
    }
    
    toString(): string {
        return String(this.type) + " " + this.dataToString();
    }
    
    dataToString(): string {
        if(!this.data) {
            return "EMPTY";
        }
        else if(Array.isArray(this.data) && !this.data.length) {
            return "EMPTY";
        } 
        else if(this.type === WktDataType.Point) {
            return "(" + wktPointToString(<WktPoint> this.data) + ")";
        }
        else if(this.type === WktDataType.MultiPoint) {
            return wktPointListToString(<WktPoint[]> this.data);
        }
        else if(this.type === WktDataType.LineString) {
            return wktPointListToString(<WktPoint[]> this.data);
        }
        else if(this.type === WktDataType.MultiLineString) {
            return wktPointListListToString(<WktPoint[][]> this.data);
        }
        else if(this.type === WktDataType.Polygon) {
            return wktPointListListToString(<WktPoint[][]> this.data);
        }
        else if(this.type === WktDataType.MultiPolygon) {
            return wktPointListListListToString(<WktPoint[][][]> this.data);
        }
        else if(this.type === WktDataType.GeometryCollection) {
            return "(" + (<Wkt[]> this.data).map(toString).join(", ") + ")";
        }
        else {
            throw new Error("Unknown WKT data type.");
        }
    }
}

export namespace Wkt {
    export type Point = WktPoint;
    export type DataType = WktDataType;
    export type Data = WktData;
    export const DataType = WktDataType;
}

export default Wkt;
